import { useNavigate } from 'react-router-dom';
import ProductQuantityIncrement from './ProductQuantityIncrement';
import { useContext, useEffect, useRef, useState } from 'react';
import LoadingButton from './LoadingButton';
import CustomModal from './CustomModal';
import { UserContext } from '../context/userContext';
import { Tooltip } from './Tooltip';
import { AlertIcon } from '../assets/svgImages';
import { ProductCardsProps, OutOfStock } from '../interface/interface';
import OutOfStockOverlay from './OutOfStockOverlay';
import { AllowedProducts } from './AllowedProducts';

const ProductCards = (props: ProductCardsProps) => {
  const {
    product,
    addProductToCart,
    removeProductFromCart,
    substitueCard = false,
    allowedProduct = false,
  } = props;

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [openSubstituteModal, setSubstituteModal] = useState<boolean>(false);
  const [suggestAccessoriesModal, setSuggestAccessories] =
    useState<boolean>(false);
  const [addProductModalLoading, setAddProductModalLoading] =
    useState<boolean>(false);
  const { userDetail, iseRxDown } = useContext(UserContext);

  const [showTooltip, setShowTooltip] = useState(false);
  const [showInvalidMailTooltip, setShowInvalidMailTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const productNameRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (productNameRef.current) {
        const isOverflowing =
          productNameRef.current.scrollWidth >
            productNameRef.current.clientWidth ||
          productNameRef.current.scrollHeight >
            productNameRef.current.clientHeight;
        setIsOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  return (
    <li
      className="col-span-1 flex flex-col rounded-lg border border-secondary-darkred-50 
      border-solid bg-white text-center shadow px-5 py-4 cursor-pointer relative h-full"
      key={product.id}
    >
      <div
        className="flex flex-1 flex-col justify-around"
        onClick={() =>
          navigate(`/product/${product.product_code}`, {
            state: {
              from: substitueCard ? '/home' : window.location.pathname,
              totalQty: product.productMaxQty,
              substituteProduct: product.substituteProduct,
              isSubstitute: substitueCard || allowedProduct,
            },
          })
        }
      >
        <div className="flex justify-between text-sm 3xl:text-base text-left font-bold text-secondary-cornflowerblue-600 capitalize">
          <p className="mr-2">{product.product_brand}</p>
          <p>${product.price}</p>
        </div>
        <div className="flex justify-between text-xs font-semibold text-grey-500 mb-1 uppercase items-center">
          <p
            className="text-left mr-1 md:two-line-ellipsis relative"
            ref={productNameRef}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {product.name}
          </p>
          {isOverflowing && showTooltip && (
            <Tooltip
              text={product.name}
              className="z-50 product-name-tooltip"
            />
          )}
          <p>
            {product.weight}
            {product.unit}
          </p>
        </div>
        <div className="h-44 w-full flex justify-center relative">
          <img
            className="rounded-lg h-44"
            src={product.image_url}
            alt="product-image"
            style={{ objectFit: 'contain' }}
            id={`product-image-${product.product_code}`}
          />
          <OutOfStockOverlay
            outOfStock={product.outOfStock}
            inventoryQuantity={product.inventory_quantity}
            hasSubstitute={Boolean(product?.substituteProduct)}
            isGuest={false}
          />
        </div>
      </div>
      <div>
        {product.orderedQuantity > 0 || substitueCard ? (
          <ProductQuantityIncrement
            productCode={product.product_code}
            orderedQuantity={product.orderedQuantity}
            productOutOfStock={product.outOfStock}
            maxQuantity={product.productMaxQty}
            addProductToCart={addProductToCart}
            removeProductFromCart={removeProductFromCart}
          />
        ) : userDetail?.supportingParams?.hasValidEmail ? (
          <>
            <LoadingButton
              buttonText="Add to Cart"
              isLoading={loading}
              buttonIcon={<>{'🛒'}</>}
              isDisabled={
                product.outOfStock !== OutOfStock.false ||
                !userDetail?.supportingParams?.isUserAllowedToOrder ||
                iseRxDown
              }
              onClick={async () => {
                setLoading(true);
                await addProductToCart(product.product_code);
                setLoading(false);
              }}
            />
            {allowedProduct && product.allowedProducts && (
              <AllowedProducts
                product={product}
                addProductToCart={addProductToCart}
                removeProductFromCart={removeProductFromCart}
              />
            )}
          </>
        ) : (
          <div className="relative">
            <button
              className="bg-primary-red-400 text-white text-sm rounded p-3 flex items-center mx-auto w-full justify-center cursor-not-allowed"
              onMouseEnter={() => setShowInvalidMailTooltip(true)}
              onMouseLeave={() => setShowInvalidMailTooltip(false)}
            >
              <span>
                <AlertIcon className="fill-white" />
              </span>
              <span className="ml-1.5">Ordering Temporarily Unavailable</span>
            </button>
            {showInvalidMailTooltip && (
              <Tooltip
                text="You cannot place orders as your email is invalid. Please update your email address in your profile. 
                Note, this will also change your login username."
                className="z-50 top-14 !w-full xl:!w-[350px]"
              />
            )}
          </div>
        )}
        {!substitueCard &&
          product.substituteProduct &&
          !product.substitutions_not_available &&
          userDetail?.supportingParams?.hasValidEmail && (
            <div className="absolute bottom-4 left-0 w-full">
              <button
                className="w-11/12 bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3
              rounded-lg text-white text-sm font-semibold
              hover:text-secondary-cornflowerblue-600 hover:bg-white
              disabled:bg-slate-400 disabled:border-slate-400 disabled:text-white disabled:cursor-not-allowed"
                onClick={() => setSubstituteModal(true)}
                disabled={!userDetail?.supportingParams?.isUserAllowedToOrder}
              >
                Order substitution
              </button>
              <CustomModal
                isOpen={openSubstituteModal}
                onClose={() => setSubstituteModal(false)}
                header={''}
                footer={''}
                modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
              >
                <div>
                  <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
                    Substitute product for:{' '}
                    <span className="font-semibold">{product.name}</span>
                  </p>
                  <ProductCards
                    product={product.substituteProduct}
                    addProductToCart={(id: string) =>
                      addProductToCart(id, product.product_code)
                    }
                    removeProductFromCart={removeProductFromCart}
                    substitueCard={true}
                    isAccessories={false}
                    allowedProduct={false}
                  />
                </div>
              </CustomModal>
            </div>
          )}
        <div className="absolute text-start bottom-4 left-0 w-full z-40">
          <CustomModal
            isOpen={suggestAccessoriesModal}
            onClose={async () => {
              setLoading(true);
              setSuggestAccessories(false);
              await addProductToCart(product.product_code);
              setLoading(false);
            }}
            header={'Dried Herb Product Added!'}
            footer={
              <>
                <LoadingButton
                  isLoading={addProductModalLoading}
                  className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
                  onClick={async () => {
                    setAddProductModalLoading(true);
                    await addProductToCart(product.product_code);
                    setAddProductModalLoading(false);
                    navigate('/non-script-range');
                  }}
                  buttonText="Proceed"
                />
                <button
                  className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
                  onClick={async () => {
                    setSuggestAccessories(false);
                    setLoading(true);
                    await addProductToCart(product.product_code);
                    setLoading(false);
                  }}
                >
                  Stay on this page
                </button>
              </>
            }
            modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
          >
            <div className="text-start">
              Would you like to purchase required accessories along with this ?
            </div>
          </CustomModal>
        </div>
      </div>
    </li>
  );
};

export default ProductCards;
